import { Button, Popconfirm, Progress, Tooltip } from "antd";
import { CheckOutlined, CloseOutlined, DatabaseOutlined, ExclamationOutlined, MinusOutlined, UploadOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { useBackgroundJobs } from "../../context/BackgroundJobContext";
import { canManageBackups, formatDuration, formatNumber, isJobActive, jobPercent, jobTitle, phaseLabel } from "./jobUtils";

// Remaining time from the progress rate over the last 30 seconds.
const useEta = (key, percent) => {
  const samples = useRef([]);
  const lastKey = useRef(key);
  if (lastKey.current !== key) {
    samples.current = [];
    lastKey.current = key;
  }
  const now = Date.now();
  const s = samples.current;
  if (!s.length || s[s.length - 1].p !== percent) s.push({ t: now, p: percent });
  while (s.length > 2 && now - s[0].t > 30000) s.shift();
  if (s.length < 2 || percent >= 99) return null;
  const rate = (s[s.length - 1].p - s[0].p) / ((s[s.length - 1].t - s[0].t) / 1000);
  return rate > 0 ? (100 - percent) / rate : null;
};

const compact = (n) => {
  n = Number(n || 0);
  if (n >= 1e6) return `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}M`;
  if (n >= 1e4) return `${Math.round(n / 1e3)}K`;
  return n.toLocaleString();
};

const RESTORE_STEPS = ["validate", "undo_backup", "load", "verify", "swap"];

const TONES = {
  active: { icon: "bg-gradient-to-br from-blue-500 to-indigo-500 text-white", pill: "bg-blue-50 text-blue-600", bar: { from: "#3b82f6", to: "#6366f1" } },
  success: { icon: "bg-gradient-to-br from-emerald-400 to-green-500 text-white", pill: "", bar: "#22c55e" },
  exception: { icon: "bg-gradient-to-br from-rose-400 to-red-500 text-white", pill: "", bar: "#ef4444" },
  warning: { icon: "bg-gradient-to-br from-amber-400 to-orange-500 text-white", pill: "bg-orange-50 text-orange-600", bar: "#f59e0b" },
};

const NOTE_TONES = {
  info: "bg-slate-50 text-slate-600",
  warn: "bg-amber-50 text-amber-700",
  error: "bg-red-50 text-red-600",
};

const JobProgressWidget = () => {
  const { server, finished, importState, cancelJob, dismissFinished, resetImport } = useBackgroundJobs();
  const [collapsed, setCollapsed] = useState(false);
  const [hiddenImportAt, setHiddenImportAt] = useState(null);
  const job = isJobActive(server.job) ? server.job : null;
  const imp = importState;
  const importRunning = ["reading", "uploading"].includes(imp.phase);
  const importFinished = ["done", "failed"].includes(imp.phase) && hiddenImportAt !== imp.finishedAt;
  const showFinished = !!finished;
  const restoring = job?.type === "restore";

  const percent = job ? jobPercent(job) : importRunning ? imp.progress || 0 : 100;
  const eta = useEta(job ? `${job.id}-${job.phase}` : `import-${imp.startedAt}`, percent);

  // Auto-hide successful backups after 10 seconds; failures and finished restores
  // (which need a page reload) stay until dismissed.
  useEffect(() => {
    if (!job && !importRunning && showFinished && finished.status === "ready" && finished.type === "backup") {
      const t = setTimeout(dismissFinished, 10000);
      return () => clearTimeout(t);
    }
  }, [job, importRunning, showFinished, finished, dismissFinished]);
  useEffect(() => {
    if (!job && imp.phase === "done" && !imp.failedChunks && importFinished) {
      const t = setTimeout(() => setHiddenImportAt(imp.finishedAt), 10000);
      return () => clearTimeout(t);
    }
  }, [job, imp.phase, imp.failedChunks, imp.finishedAt, importFinished]);

  if (!job && !importRunning && !importFinished && !showFinished) return null;

  const onCancel = async (id) => {
    try {
      const res = await cancelJob(id);
      toast.success(res?.message || "Cancelling…");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Could not cancel the job");
    }
  };
  const hideImport = () => {
    setHiddenImportAt(imp.finishedAt);
    if (imp.phase === "failed") resetImport();
  };

  // Every state fills the same compact layout: icon, title, subtitle, two meta lines, note, actions.
  let tone = "active";
  let icon = <DatabaseOutlined />;
  let title;
  let subtitle;
  let meta = null; // [left, right]
  let note = null; // { tone, text }
  let actions = null;
  let closable = false;

  if (job) {
    title = jobTitle(job);
    const queued = job.status === "queued";
    if (job.type === "restore" && !queued) {
      const step = Math.max(0, RESTORE_STEPS.indexOf(job.phase === "swapped" ? "swap" : job.phase));
      subtitle = `Step ${step + 1}/${RESTORE_STEPS.length} · ${phaseLabel(job)}`;
    } else {
      subtitle = queued ? (server.import_active ? "Waiting for the import to finish…" : "Starting…") : "Saving every table";
    }
    if (!queued && job.progress_table_count) {
      meta = [
        `Table ${job.progress_table_index}/${job.progress_table_count}${job.progress_table ? ` · ${job.progress_table}` : ""}`,
        `${compact(job.progress_done)} / ${compact(job.progress_total)} rows${eta != null ? ` · ~${formatDuration(eta)}` : ""}`,
      ];
    }
    if (queued && job.age_seconds > 90 && !server.worker_alive) {
      tone = "warning";
      note = { tone: "warn", text: "Backup worker is not running. Contact the administrator." };
    } else if (job.type === "restore") {
      note = { tone: "warn", text: "The app is read-only while restoring." };
    }
    if (!["swap", "swapped"].includes(job.phase) && canManageBackups()) {
      actions = (
        <Popconfirm title="Cancel this job?" description="Live data is not changed by cancelling." onConfirm={() => onCancel(job.id)}>
          <Button size="small" type="text" danger className="!px-2 text-xs">Cancel</Button>
        </Popconfirm>
      );
    }
  } else if (importRunning) {
    icon = <UploadOutlined />;
    title = imp.fileInfo?.name || "Importing file";
    subtitle = imp.phase === "uploading" ? "Importing vehicle specs" : imp.step;
    if (imp.phase === "uploading") {
      meta = [`Chunk ${imp.currentChunk}/${imp.totalChunks}`, `${formatNumber(imp.totalStats?.received)} rows processed`];
    }
  } else if (importFinished) {
    const ok = imp.phase === "done" && !imp.failedChunks;
    tone = ok ? "success" : "exception";
    icon = ok ? <CheckOutlined /> : <ExclamationOutlined />;
    closable = true;
    title = ok ? "Import finished" : imp.phase === "done" ? "Import finished with errors" : "Import failed";
    subtitle = imp.fileInfo?.name;
    if (imp.phase === "done") {
      meta = [
        `${formatNumber(imp.totalStats?.received)} rows processed`,
        imp.failedChunks ? `${imp.failedChunks} chunk(s) failed` : `${imp.totalChunks} chunks`,
      ];
    } else {
      note = { tone: "error", text: imp.error };
    }
    actions = (
      <Link to="/vehicle-specs/upload-data">
        <Button size="small" type="link" className="!px-2 text-xs">View details</Button>
      </Link>
    );
  } else {
    const ok = finished.status === "ready";
    tone = ok ? "success" : finished.status === "cancelled" ? "warning" : "exception";
    icon = ok ? <CheckOutlined /> : <ExclamationOutlined />;
    closable = true;
    title = `${finished.type === "restore" ? `Restored to backup #${finished.restore_of_id}` : `Backup #${finished.id}`}${ok ? (finished.type === "restore" ? "" : " completed") : ` ${finished.status}`}`;
    subtitle = finished.elapsed_seconds ? `Took ${formatDuration(finished.elapsed_seconds)}` : null;
    if (!ok) {
      note = { tone: "error", text: finished.error };
    } else if (finished.type === "backup") {
      meta = [`${formatNumber(finished.total_rows)} rows`, `${finished.tables?.length || 0} tables`];
    } else {
      meta = [`${finished.result?.tables_restored ?? "-"} tables restored`, `Undo point #${finished.undo_backup_id}`];
      note = { tone: "info", text: "Reload to see the restored data." };
    }
    actions = (
      <>
        {canManageBackups() && (
          <Link to="/data-backups">
            <Button size="small" type="text" className="!px-2 text-xs">Backups</Button>
          </Link>
        )}
        {ok && finished.type === "restore" && (
          <Button size="small" type="primary" className="text-xs" onClick={() => window.location.reload()}>Reload</Button>
        )}
      </>
    );
  }

  const status = tone === "success" ? "success" : tone === "exception" ? "exception" : "active";
  const t = TONES[tone];

  // Restoration mode: grey out and block the whole app (navbar and this card stay above it).
  const overlay = restoring && (
    <div className="fixed inset-0 z-[999] flex cursor-wait items-center justify-center bg-gray-700/50 backdrop-blur-[2px]">
      <div className="mx-4 max-w-sm rounded-2xl bg-white px-6 py-5 text-center shadow-xl">
        <div className="text-base font-semibold text-slate-800">Restoration mode</div>
        <div className="mt-1 text-sm text-slate-500">The database is being restored. The app is locked until it finishes.</div>
      </div>
    </div>
  );

  if (collapsed) {
    return (
      <>
      {overlay}
      <Tooltip title={`${title} · ${percent}%`} placement="left">
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="fixed bottom-16 right-6 z-[1000] cursor-pointer rounded-full border-0 bg-white p-1 shadow-[0_8px_30px_-6px_rgba(15,23,42,0.35)] ring-1 ring-black/5 transition-transform hover:scale-105"
          aria-label="Show progress"
        >
          <Progress type="circle" size={44} percent={percent} status={status} strokeColor={t.bar} strokeWidth={10} />
        </button>
      </Tooltip>
      </>
    );
  }

  return (
    <>
    {overlay}
    <div className="fixed bottom-16 right-6 z-[1000] w-[320px] max-w-[calc(100vw-32px)] rounded-2xl bg-white/95 p-3.5 shadow-[0_12px_40px_-12px_rgba(15,23,42,0.35)] ring-1 ring-black/5 backdrop-blur">
      <div className="flex items-center gap-2.5">
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-sm shadow-sm ${t.icon}`}>{icon}</div>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-[13px] font-semibold text-slate-800" title={title}>{title}</div>
          {subtitle && <div className="mt-0.5 truncate text-[11px] text-slate-500" title={subtitle}>{subtitle}</div>}
        </div>
        {status === "active" && (
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${t.pill}`}>{percent}%</span>
        )}
        <Button
          type="text"
          size="small"
          className="-mr-1 text-slate-400"
          icon={closable ? <CloseOutlined /> : <MinusOutlined />}
          aria-label={closable ? "Close" : "Minimise"}
          onClick={closable ? (importFinished ? hideImport : dismissFinished) : () => setCollapsed(true)}
        />
      </div>

      {status === "active" && (
        <Progress className="!mb-0 mt-3" percent={percent} showInfo={false} size={["100%", 6]} strokeColor={t.bar} trailColor="#eef2f7" />
      )}

      {meta && (
        <div className="mt-1.5 flex items-center justify-between gap-3 text-[11px] text-slate-500">
          <span className="truncate" title={meta[0]}>{meta[0]}</span>
          <span className="shrink-0 tabular-nums">{meta[1]}</span>
        </div>
      )}

      {(note || actions) && (
        <div className="mt-2.5 flex items-center gap-2">
          {note && <div className={`min-w-0 flex-1 rounded-lg px-2.5 py-1.5 text-[11px] leading-snug ${NOTE_TONES[note.tone]}`}>{note.text}</div>}
          {actions && <div className="ml-auto flex shrink-0 items-center gap-1">{actions}</div>}
        </div>
      )}
    </div>
    </>
  );
};

export default JobProgressWidget;
